import './templates.professional-caregiver.scss';
import {Alert, Box, Stack} from '@mui/material';
import {useEffect, useMemo, useState} from 'react';
import {useOffice} from '../../../../../../contexts/office/context';
import {useObserveViewport} from '../../../../../../hooks';
import {appSizes} from '../../../../../../utils/constants';
import {useParams} from 'react-router-dom';
import {EntityTemplatesRepository} from '../../../../../../repositories/entity-templates';
import {EntityTemplate} from '../../../../../../backend/responses/entity-template';
import {EntityActivitiesRepository} from '../../../../../../repositories/entity-activities';
import {useFetchAllItems} from '../../../../../../hooks/fetch-all-items';
import TemplatesHeader from './templates-edit-header';
import {ActivitySkeleton} from '../../../../../../components/care-plan/activities-group-entry/skeleton';
import {translations} from '../../../../../../i18n/translations';
import {useTranslation} from 'react-i18next';
import {convertArrayToIndex} from '../../../../../../utils/functions';
import {ActivitiesGroup} from '../../../../../../backend/interfaces/activities-group';
import {CarePlanActivitiesGroup} from '../../../../../../backend/interfaces/care-plan-activities-group';
import {LoadingConfigurationsType} from '../../../../../../enums';
import {TemplateEditCard} from './templates-edit-card';
import {useSnackBar} from '../../../../../../contexts/snack-bar/context';
import {TemplatesEditNameModal} from './templates-edit-name-modal';
import {BsInfoCircle} from 'react-icons/bs';

export const TemplateEditScreen = () => {
  const {t} = useTranslation();
  const officeContext = useOffice();
  const snackBarContext = useSnackBar();
  let {entityId, templateId}: {entityId: string; templateId: string} = useParams();
  const [template, setTemplate] = useState<EntityTemplate | undefined>();
  const [loadingTemplate, setLoadingTemplate] = useState<LoadingConfigurationsType>();
  const [editModeActivity, setEditModeActivity] = useState<string>();
  const [isEditingName, setIsEditingName] = useState<boolean>(false);
  const [isSubmiting, setIsSubmiting] = useState<boolean>(false);
  const {isRunningMobile} = useObserveViewport();
  const calculatedContainerMaxWidth = useMemo(() => {
    let sum = 0;

    let appContainersMargins = 16 * 2;

    // hamburguer
    if (!isRunningMobile) {
      if (officeContext.state.drawerOpened && !officeContext.state.drawerCollapsed) {
        sum += appSizes.sideHamburguerMenu.width.open;
      } else {
        sum += appSizes.sideHamburguerMenu.width.closed;
      }
    } else if (officeContext.state.drawerCollapsed) {
      appContainersMargins += 16;
    }

    // sidemenu
    if (!officeContext.state.sideNavigationDrawerCollapsed) {
      sum += appSizes.genericCollapsibleSidePanel.width.open;
    } else {
      sum += appSizes.genericCollapsibleSidePanel.width.closed;
    }

    return `calc(100vw - ${sum}px - ${appContainersMargins}px)`;
  }, [
    isRunningMobile,
    officeContext.state.drawerCollapsed,
    officeContext.state.drawerOpened,
    officeContext.state.sideNavigationDrawerCollapsed,
  ]);
  const {
    items: activitiesGroups,
    isLoading: isLoadingActivityGroups,
    error,
  } = useFetchAllItems({
    fetchFunction: ({limit, skip}) => {
      return EntityActivitiesRepository.instance.getGroups(
        skip,
        limit,
        entityId,
        template?.activitiesSectorId ?? ''
      );
    },
    deps: [template?.activitiesSectorId],
    enabled: !!template?.activitiesSectorId,
  });
  const indexedActivitiesGroup = useMemo(() => {
    if (!activitiesGroups) {
      return;
    }

    return convertArrayToIndex<ActivitiesGroup>(activitiesGroups, 'id');
  }, [activitiesGroups]);

  useEffect(() => {
    const getTemplateData = async (entityId: string, templateId: string) => {
      setLoadingTemplate(LoadingConfigurationsType.Loading);
      EntityTemplatesRepository.instance
        .getTemplate(entityId, templateId)
        .then((data) => {
          setTemplate(data);

          setLoadingTemplate(LoadingConfigurationsType.Loaded);
        })
        .catch((e) => {
          console.error(e);
          setLoadingTemplate(LoadingConfigurationsType.Error);
        });
    };
    getTemplateData(entityId, templateId);
  }, [entityId, templateId]);

  useEffect(() => {
    if (!template) return;
    let editing = '';
    if (indexedActivitiesGroup) {
      template.activitiesGroups?.forEach((activitiesGroup) => {
        if (indexedActivitiesGroup[activitiesGroup.activitiesGroupId]?.displayOrder === 1) {
          editing = activitiesGroup.id;
        }
      });
    }
    if (!editing && template.activitiesGroups && template.activitiesGroups[0])
      editing = template.activitiesGroups[0].id;
    setEditModeActivity(editing);
  }, [indexedActivitiesGroup, template]);

  const handleUpdateActivityType = (updatedEntry: CarePlanActivitiesGroup) => {
    setTemplate((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        activitiesGroups: prev.activitiesGroups?.map((prevActivitiesGroup) => {
          if (prevActivitiesGroup.id === updatedEntry.id) {
            return updatedEntry;
          }
          return prevActivitiesGroup;
        }),
      };
    });
  };

  const handleAddedEntrySideEffect = (carePlanActivitiesGroup: CarePlanActivitiesGroup) => {
    // unset previous
    setEditModeActivity(undefined);

    if (!template) return;

    // shallow copy the current careplan
    const updatedTemplate = {...template};
    if (!updatedTemplate.activitiesGroups) updatedTemplate.activitiesGroups = [];

    // add new entry as first
    updatedTemplate.activitiesGroups?.unshift(carePlanActivitiesGroup);

    // set entry to current careplan
    setTemplate(updatedTemplate);

    // focus on new entry
    setEditModeActivity(carePlanActivitiesGroup.id);
  };

  const handleCreateActivityGroup = (newActivitiesGroup: ActivitiesGroup) => {
    EntityActivitiesRepository.instance
      .getGroupCarePlanConfigurations(
        entityId,
        newActivitiesGroup.activitiesSectorId,
        newActivitiesGroup.id
      )
      .then((response) => {
        EntityTemplatesRepository.instance
          .addTemplateActivityGroup(entityId, templateId, {
            activitiesGroupId: newActivitiesGroup.id ?? '',
            configurations: response
              ? response.map((configuration) => {
                  return {
                    carePlanConfigurationId: configuration.id ?? '',
                    carePlanConfigurationValue: null,
                  };
                })
              : undefined,
          })
          .then((response) => {
            if (response) handleAddedEntrySideEffect(response);
            snackBarContext.setOfficeToastNotification({
              severity: 'success',
              message: t(translations.activities_group_entry.snackbar.create.success),
            });
          })
          .catch((_error) => {
            snackBarContext.setOfficeToastNotification({
              severity: 'error',
              message: t(translations.activities_group_entry.snackbar.create.error),
            });
          });
      })
      .catch((_error) => {
        snackBarContext.setOfficeToastNotification({
          severity: 'error',
          message: t(translations.activities_group_entry.snackbar.create.error),
        });
      });
  };

  const handleUpdateName = (name: string, description: string) => {
    setIsSubmiting(true);
    EntityTemplatesRepository.instance
      .updateTemplate(entityId, templateId, name, description)
      .then((response) => {
        setTemplate(response);
        setIsSubmiting(false);
        setIsEditingName(false);
        snackBarContext.setOfficeToastNotification({
          severity: 'success',
          message: t(
            translations.office.professional_caregiver.settings.templates.snackbar
              .edit_name_success_message,
            {name: response.name}
          ),
        });
      })
      .catch((error) => {
        console.error(error);
        snackBarContext.setOfficeToastNotification({
          severity: 'error',
          message: t(
            translations.office.professional_caregiver.settings.templates.snackbar
              .edit_name_error_message,
            {name: name}
          ),
        });
      });
  };

  return (
    <Stack
      className="template-edit"
      rowGap={2}
      sx={{
        maxWidth: calculatedContainerMaxWidth,
        transition: 'max-width 0.2s ease-in-out',
      }}
    >
      {/* HEADER */}
      <TemplatesHeader
        isLoading={loadingTemplate === LoadingConfigurationsType.Loading || isLoadingActivityGroups}
        activitiesGroups={activitiesGroups}
        name={template?.name ?? ''}
        description={template?.description}
        template={template}
        onActivitiesGroupSelected={(activitiesGroup: ActivitiesGroup) =>
          handleCreateActivityGroup(activitiesGroup)
        }
        onEditName={() => setIsEditingName(true)}
      />

      <Alert
        className="templates-alert-info"
        severity="info"
        variant="outlined"
        icon={<BsInfoCircle fontSize="16px" />}
      >
        {t(translations.office.professional_caregiver.settings.templates.alert)}
      </Alert>

      {isEditingName && template && (
        <TemplatesEditNameModal
          template={template}
          open
          submitting={isSubmiting}
          onClose={() => setIsEditingName(false)}
          onUpdate={handleUpdateName}
        />
      )}

      {/* RESULTS */}
      {(loadingTemplate === LoadingConfigurationsType.Loading || isLoadingActivityGroups) && (
        <Box className="result-wrapper">
          {new Array(3).fill(1).map((_props, index) => (
            <ActivitySkeleton key={`activity_skeleton_${index}`} />
          ))}
        </Box>
      )}

      {(loadingTemplate === LoadingConfigurationsType.Error || error) && (
        <Alert severity="error">
          {t(translations.seniors.edit.senior_dcm.careplan.load_error)}
        </Alert>
      )}

      {loadingTemplate === LoadingConfigurationsType.Loaded &&
        !isLoadingActivityGroups &&
        (!template?.activitiesGroups || template?.activitiesGroups.length === 0) && (
          <Box className="result-wrapper result-wrapper-center">
            <Box className="result-wrapper-empty">
              <h3 className="title">
                {t(translations.seniors.edit.senior_dcm.careplan.activities.empty_state.title)}
              </h3>
              <h5 className="subtitle">
                {t(translations.seniors.edit.senior_dcm.careplan.activities.empty_state.subtitle)}
              </h5>
            </Box>
          </Box>
        )}

      {loadingTemplate === LoadingConfigurationsType.Loaded &&
        !isLoadingActivityGroups &&
        template &&
        indexedActivitiesGroup && (
          <Box className="result-wrapper">
            {template.activitiesGroups?.map((activitiesGroup: CarePlanActivitiesGroup) => {
              // Extract display order from activity group configuration
              const displayOrder =
                indexedActivitiesGroup[activitiesGroup.activitiesGroupId]?.displayOrder || 1;
              return (
                <TemplateEditCard
                  key={`template-edit-card-${template.id}-${activitiesGroup.id}`}
                  order={displayOrder}
                  activitiesGroup={activitiesGroup}
                  activitiesSectorId={template.activitiesSectorId ?? ''}
                  editModeActivity={editModeActivity === activitiesGroup.id}
                  setEditModeActivity={setEditModeActivity}
                  onDeleteActivityGroup={(activityGroupId) => {
                    setTemplate((prev) => {
                      if (!prev) return prev;
                      return {
                        ...prev,
                        activitiesGroups: prev.activitiesGroups?.filter(
                          (prevActivitiesGroup) => prevActivitiesGroup.id !== activityGroupId
                        ),
                      };
                    });
                  }}
                  onUpdateActivityGroup={(updatedEntry) => {
                    setTemplate((prev) => {
                      if (!prev) return prev;
                      return {
                        ...prev,
                        activitiesGroups: prev.activitiesGroups?.map((prevActivitiesGroup) => {
                          if (prevActivitiesGroup.id === updatedEntry.id) {
                            return updatedEntry;
                          }
                          return prevActivitiesGroup;
                        }),
                      };
                    });
                  }}
                  onUpdateActivityGroupType={handleUpdateActivityType}
                />
              );
            })}
          </Box>
        )}
    </Stack>
  );
};
